import React from 'react';
import { Calendar, MapPin, Monitor, FileText, Heart } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Event, EventStatus, EventFormat } from '../../types/event.d';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../lib/utils';

interface EventCardProps {
  event: Event;
  isFavorite?: boolean;
  onToggleFavorite?: (event: Event) => void;
  onViewDetails?: (event: Event) => void;
  onAction?: (event: Event) => void;
}

const getStatusVariant = (status: EventStatus) => {
  switch (status) {
    case EventStatus.CONFIRMED:
      return 'confirmed';
    case EventStatus.COMPLETED:
      return 'completed';
    case EventStatus.PRESENCE_VALIDATED:
      return 'presence_validated';
    case EventStatus.PENDING:
      return 'warning';
    case EventStatus.CANCELLED:
      return 'destructive';
    default:
      return 'default';
  }
};

const EventCard: React.FC<EventCardProps> = ({ event, isFavorite, onToggleFavorite, onViewDetails, onAction }) => {
  const eventDate = new Date(event.date);

  return (
    <div 
      onClick={() => onViewDetails?.(event)}
      className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group cursor-pointer"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(event.status)}>
            {event.status}
          </Badge>
          <span className="text-xs text-slate-400 font-mono">ID: {event.id}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs font-medium text-rajapiBlue bg-blue-50 px-2 py-1 rounded">
            {event.format === EventFormat.ONLINE ? (
              <>
                <Monitor size={12} />
                <span>EN LIGNE</span>
              </>
            ) : (
              <>
                <MapPin size={12} />
                <span>PRÉSENTIEL</span>
              </>
            )}
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite?.(event);
            }}
            className={cn(
              "p-1.5 rounded-full transition-all duration-300",
              isFavorite 
                ? "bg-red-50 text-red-500 shadow-sm" 
                : "text-slate-300 hover:text-red-400 hover:bg-slate-50"
            )}
          >
            <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
          </button>
        </div>
      </div>

      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-rajapiBlue transition-colors">
        {event.title}
      </h3>
      
      <p className="text-slate-600 text-sm line-clamp-2 mb-6 h-10">
        {event.description}
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <Calendar size={14} className="text-slate-400" />
          <span>{format(eventDate, 'dd MMM yyyy', { locale: fr })}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <MapPin size={14} className="text-slate-400" />
          <span className="truncate">{event.location}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <Monitor size={14} className="text-slate-400" />
          <span>{event.format}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <FileText size={14} className="text-slate-400" />
          <span>{event.documents?.length || 0} Fichiers</span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
        {event.status === EventStatus.COMPLETED || event.status === EventStatus.PRESENCE_VALIDATED ? (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-9 text-rajapiBlue border-rajapiBlue hover:bg-blue-50"
              onClick={(e) => {
                e.stopPropagation();
                // Logic for support download could go here
              }}
            >
              {event.status === EventStatus.PRESENCE_VALIDATED ? 'Récupérer certificat' : 'Télécharger supports'}
            </Button>
            <Button 
              size="sm" 
              className="h-9 bg-rajapiBlue hover:bg-rajapiNavyBlue text-white" 
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(event);
              }}
            >
              Donner mon avis
            </Button>
          </>
        ) : event.status === EventStatus.CONFIRMED ? (
          <Button 
            variant="destructive" 
            size="sm" 
            className="h-9 bg-rajapiRed hover:bg-red-600" 
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(event);
            }}
          >
            Annuler l'inscription
          </Button>
        ) : (
          <Button 
            size="sm" 
            className="h-9 bg-rajapiBlue hover:bg-rajapiNavyBlue text-white" 
            onClick={(e) => {
              e.stopPropagation();
              onAction?.(event);
            }}
          >
            S'inscrire
          </Button>
        )}
      </div>
    </div>
  );
};

export default EventCard;
